import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { meetingsApi, type ProjectMeetings } from '@/lib/apiClient';
import { Badge, Button } from '@/components';
import { fmtSentAt } from '../lib/sentAt';
import MeetingEditor from './MeetingEditor';
import { STATUS_TONE } from './meetingForm';
import { MeetingStyles } from './meetingStyles';

/**
 * A job's Meetings tab (#644): its meetings, each with an agenda and the
 * minutes recorded against it — OAC meetings and the job's others.
 *
 * Everyone who can see the job reads them; those who can edit jobs add, edit
 * and remove them. One meeting opens from `?meeting=<id>` (so the Overview
 * feed's chip and a pasted link land on it), and a new one from `?meeting=new`.
 */
export default function MeetingsTab({ projectId }: { projectId: number }) {
  const [params, setParams] = useSearchParams();
  const q = useQuery({ queryKey: ['project-meetings', projectId], queryFn: () => meetingsApi.list(projectId) });
  const open = params.get('meeting');
  const openId = open && /^\d+$/.test(open) ? Number(open) : null;

  /** Open a meeting (or the new one), or come back to the list, keeping the tab. */
  const go = (meeting: string | null) => setParams((p) => {
    const next = new URLSearchParams(p);
    if (meeting) next.set('meeting', meeting); else next.delete('meeting');
    return next;
  }, { replace: true });

  return (
    <div className="mtg">
      <MeetingStyles />
      {q.isPending ? (
        <p className="mtg-muted">Loading the job’s meetings…</p>
      ) : q.isError || !q.data ? (
        <div className="mtg-error" role="alert">Couldn’t load the job’s meetings. {(q.error as Error | null)?.message}</div>
      ) : open === 'new' && q.data.canEdit ? (
        <MeetingEditor projectId={projectId} meetingId={null} lists={q.data} onClose={() => go(null)} onSaved={(id) => go(String(id))} />
      ) : openId ? (
        <MeetingEditor key={openId} projectId={projectId} meetingId={openId} lists={q.data} onClose={() => go(null)} onSaved={(id) => go(String(id))} />
      ) : (
        <MeetingList data={q.data} onOpen={go} />
      )}
    </div>
  );
}

function MeetingList({ data, onOpen }: { data: ProjectMeetings; onOpen: (m: string) => void }) {
  const typeLabel = (k: string) => data.types.find((t) => t.key === k)?.label ?? k;
  const statusLabel = (k: string) => data.statuses.find((s) => s.key === k)?.label ?? k;
  return (
    <div className="pw-card">
      <div className="mtg-hd">
        <h2 className="pw-card-hd">Meetings</h2>
        {data.canEdit && <Button size="sm" onClick={() => onOpen('new')}>＋ New Meeting</Button>}
      </div>
      {data.meetings.length === 0 ? (
        <p className="mtg-pad mtg-muted">
          No meetings on this job yet.{data.canEdit ? ' Add one with New Meeting to draft its agenda, then record the minutes against it.' : ''}
        </p>
      ) : (
        <ul className="mtg-list" aria-label="Meetings">
          {data.meetings.map((m) => (
            <li key={m.id}>
              <button type="button" className="mtg-row" onClick={() => onOpen(String(m.id))}>
                <span>
                  <span className="mtg-name">{m.name}</span>
                  {m.title && <span className="mtg-muted mtg-small"> · {typeLabel(m.meetingType)} #{m.meetingNumber}</span>}
                </span>
                <span>{fmtSentAt(m.startsAt)}</span>
                <span className="mtg-muted">{m.location ?? '—'}</span>
                <span><Badge label={statusLabel(m.status)} tone={STATUS_TONE[m.status] ?? 'neutral'} /></span>
                <span className="mtg-count">{m.itemCount === 0 ? 'No items' : `${m.openCount} open of ${m.itemCount}`}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
